import { CallbackProperty, PositionProperty, Cartesian3, Color, Ellipsoid, Transforms, Matrix4, ColorMaterialProperty, Math as CesiumMath, PolylineArrowMaterialProperty, ArcType, Cartesian2 } from 'cesium'
import { computed } from 'vue'
import { CesiumService } from '@/handlers/cesium.service'
import { useMissionsState } from '@/composables/Missions/useMissionsState'
import { getPolygonCentroid } from '@/composables/AreaMission/usePhotogrammetryCalcs'
import { ACTIVE_ROUTE_COLORS, WAYPOINT_COLORS } from '@/constants/missionData'
import type { Mission } from '@/types/route.types'
import type { Circle } from '@/types/route.types'
import log from '@/utils/useLogger'

const viewer = computed(() => CesiumService.viewer.value)
export const removeEntity = (id: string) => {
  try {
    const entity = viewer.value?.entities.getById(id)
    if (entity) viewer.value?.entities.remove(entity)
  } catch (error) {
    log.error('Error removing entity:', error)
  }
}

export const removeAllEntitiesForElement = (elementId: string) => {
  try {
    removeEntity(`ellipsoid-base-${elementId}`)
    removeEntity(`vertical-line-${elementId}`)
    removeEntity(`ellipsoid-top-${elementId}`)
    removeEntity(`circle-top-${elementId}`)
  } catch (error) {
    log.error('Error removing entities:', error)
  }
}

export const addWaypointEntity = (missionId: string, elementId: string) => {
  if (!viewer.value) return
  const { missions, activeMissionIndex } = useMissionsState()

  const missionIndex = missions.value.findIndex((m) => m.missionId === missionId)
  if (missionIndex === -1) return

  const elementIndex = missions.value[missionIndex].elements.findIndex((e) => e.id === elementId)
  if (elementIndex === -1) return

  const colors = activeMissionIndex.value === missionIndex ? ACTIVE_ROUTE_COLORS : WAYPOINT_COLORS
  const id = elementId
  const baseId = `ellipsoid-base-${id}`
  const verticalId = `vertical-line-${id}`
  const topId = `ellipsoid-top-${id}`
  removeEntity(baseId)
  removeEntity(verticalId)
  removeEntity(topId)
  const baseEllipsoid = viewer.value.entities.add({
    id: baseId,
    position: new CallbackProperty(() => {
      const mission = missions.value.find((m) => m.missionId === missionId)
      if (!mission) return undefined
      const element = mission.elements.find((e) => e.id === elementId)
      if (!element) return undefined
      return element.position
    }, false) as unknown as PositionProperty,
    ellipsoid: {
      radii: new Cartesian3(1, 1, 1),
      material: colors.base,
    },
  })
  const verticalLine = viewer.value.entities.add({
    id: verticalId,
    polyline: {
      positions: new CallbackProperty(() => {
        const mission = missions.value.find((m) => m.missionId === missionId)
        if (!mission) return undefined
        const element = mission.elements.find((e) => e.id === elementId)
        if (!element) return undefined
        return [element.position, element.topPosition]
      }, false),
      material: colors.line,
      width: 3,
    },
  })
  const topEllipsoid = viewer.value.entities.add({
    id: topId,
    position: new CallbackProperty(() => {
      const mission = missions.value.find((m) => m.missionId === missionId)
      if (!mission) return undefined
      const element = mission.elements.find((e) => e.id === elementId)
      if (!element) return undefined
      return element.topPosition
    }, false) as unknown as PositionProperty,
    ellipsoid: {
      radii: new Cartesian3(2, 2, 2),
      material: colors.base,
    },
  })
  return {
    base: baseEllipsoid.id,
    vertical: verticalLine.id,
    top: topEllipsoid.id,
  }
}

export const addCircleEntity = (missionId: string, elementId: string) => {
  if (!viewer.value) return
  const entities = addWaypointEntity(missionId, elementId)
  const { missions, activeMissionIndex } = useMissionsState()

  const missionIndex = missions.value.findIndex((m) => m.missionId === missionId)
  if (missionIndex === -1) return

  const elementIndex = missions.value[missionIndex].elements.findIndex((e) => e.id === elementId)
  if (elementIndex === -1) return

  const colors = activeMissionIndex.value === missionIndex ? ACTIVE_ROUTE_COLORS : WAYPOINT_COLORS
  const id = elementId
  const circleId = `circle-top-${id}`

  removeEntity(circleId)

  const circle = viewer.value.entities.add({
    id: circleId,
    position: new CallbackProperty(() => {
      const mission = missions.value.find((m) => m.missionId === missionId)
      if (!mission) return undefined
      const element = mission.elements.find((e) => e.id === elementId)
      if (!element) return undefined
      return element.topPosition
    }, false) as unknown as PositionProperty,
    polyline: {
      positions: new CallbackProperty(() => {
        const mission = missions.value.find((m) => m.missionId === missionId)
        if (!mission) return undefined
        const element = mission.elements.find((e) => e.id === elementId) as Circle
        if (!element) return undefined

        const centerPos = element.topPosition
        const radius = element.radius || 10
        const positions = []

        const ellipsoid = Ellipsoid.WGS84
        const transform = Transforms.eastNorthUpToFixedFrame(centerPos, ellipsoid)

        for (let i = 0; i <= 360; i += 10) {
          const radians = CesiumMath.toRadians(i)
          const localPosition = new Cartesian3(
            radius * Math.cos(radians),
            radius * Math.sin(radians),
            0,
          )
          const worldPosition = Matrix4.multiplyByPoint(
            transform,
            localPosition,
            new Cartesian3(),
          )
          positions.push(worldPosition)
        }

        return positions
      }, false),
      material: new ColorMaterialProperty(
        new Color(
          colors.base.red * 0.7,
          colors.base.green * 0.7,
          colors.base.blue * 0.7,
          1.0,
        ),
      ),
      width: 8,
      clampToGround: false,
    },
  })
  return {
    base: entities?.base,
    vertical: entities?.vertical,
    top: entities?.top,
    circle: circle.id,
  }
}

export const initializeEntities = () => {
  try {
    clearAllEntities()

    const { missions } = useMissionsState()
    if (missions.value.length === 0) return
    missions.value.forEach((mission) => {
      mission.elements.forEach((element) => {
        if (element.type === 'waypoint') {
          addWaypointEntity(mission.missionId, element.id)
        } else if (element.type === 'circle') {
          addCircleEntity(mission.missionId, element.id)
        }
      })
      if (mission.elements.length > 1) {
        for (let i = 0; i < mission.elements.length - 1; i++) {
          drawConnectionBetween(mission.elements[i].id, mission.elements[i + 1].id, mission)
        }
      }
    })
  } catch (error) {
    log.error('Error initializing entities:', error)
  }
}

const AREA_PATH_COLOR = Color.fromCssColorString('#FF9D00')
const AREA_START_COLOR = Color.fromCssColorString('#FF5722')
const AREA_POLY_FILL = Color.fromCssColorString('#00E676').withAlpha(0.18)
const AREA_POLY_OUTLINE = Color.fromCssColorString('#00E676').withAlpha(0.8)

// Track entity counts per mission so we can add/remove when grid line count changes
const areaEntityCounts = new Map<string, number>()

/**
 * Create all area mission entities (polygon + grid + centroid + start marker)
 * using CallbackProperty that reads from the store — same pattern as addWaypointEntity.
 * Called ONCE per mission. Entities auto-update when store data changes.
 */
export const addAreaMissionEntities = (missionId: string): string[] => {
  if (!viewer.value) return []
  const { missions } = useMissionsState()
  const entityIds: string[] = []

  const getAM = () => missions.value.find(m => m.missionId === missionId)?.areaMission
  const getAlt = () => {
    const am = getAM()
    if (!am || am.polygon.length === 0) return 0
    const avg = am.polygon.reduce((s, v) => s + (v.height || 0), 0) / am.polygon.length
    return am.parameters.altitude + avg
  }

  const _add = (id: string, def: Record<string, any>): boolean => {
    removeEntity(id)
    try {
      viewer.value!.entities.add({ id, ...def })
      entityIds.push(id)
      return true
    } catch (err) {
      log.error(`Failed to add area entity ${id}:`, err)
      return false
    }
  }

  // ── Polygon fill ──
  _add(`area-poly-fill-${missionId}`, {
    polygon: {
      hierarchy: new CallbackProperty(() => {
        const am = getAM()
        if (!am || am.polygon.length < 3) return undefined
        return { positions: am.polygon.map(v => Cartesian3.fromDegrees(v.longitude, v.latitude, 0)) }
      }, false) as any,
      material: new ColorMaterialProperty(AREA_POLY_FILL),
      classificationType: 2,
    },
  })

  // ── Polygon outline ──
  _add(`area-poly-outline-${missionId}`, {
    polyline: {
      positions: new CallbackProperty(() => {
        const am = getAM()
        if (!am || am.polygon.length < 3) return []
        const pts = am.polygon.map(v => Cartesian3.fromDegrees(v.longitude, v.latitude, 0))
        return [...pts, pts[0]]
      }, false),
      material: new ColorMaterialProperty(AREA_POLY_OUTLINE),
      width: 2,
      clampToGround: true,
    },
  })

  // ── Grid lines (pre-allocate slots, hide unused) ──
  const MAX_GRID_LINES = 200
  for (let i = 0; i < MAX_GRID_LINES; i++) {
    _add(`area-line-${missionId}-${i}`, {
      polyline: {
        positions: new CallbackProperty(() => {
          const am = getAM()
          if (!am || i >= am.gridLines.length) return []
          const alt = getAlt()
          const line = am.gridLines[i]
          return [
            Cartesian3.fromDegrees(line.start.longitude, line.start.latitude, alt),
            Cartesian3.fromDegrees(line.end.longitude, line.end.latitude, alt),
          ]
        }, false),
        show: new CallbackProperty(() => {
          const am = getAM()
          return !!(am && i < am.gridLines.length)
        }, false) as any,
        width: 8,
        material: new PolylineArrowMaterialProperty(AREA_PATH_COLOR),
        arcType: ArcType.NONE,
      },
    })
  }

  // ── Connectors between grid lines ──
  for (let i = 0; i < MAX_GRID_LINES - 1; i++) {
    _add(`area-conn-${missionId}-${i}`, {
      polyline: {
        positions: new CallbackProperty(() => {
          const am = getAM()
          if (!am || i >= am.gridLines.length - 1) return []
          const alt = getAlt()
          const cur = am.gridLines[i]
          const nxt = am.gridLines[i + 1]
          return [
            Cartesian3.fromDegrees(cur.end.longitude, cur.end.latitude, alt),
            Cartesian3.fromDegrees(nxt.start.longitude, nxt.start.latitude, alt),
          ]
        }, false),
        show: new CallbackProperty(() => {
          const am = getAM()
          return !!(am && i < am.gridLines.length - 1)
        }, false) as any,
        width: 3,
        material: AREA_PATH_COLOR,
        arcType: ArcType.NONE,
      },
    })
  }

  // ── Centroid connector ──
  _add(`area-centroid-${missionId}`, {
    polyline: {
      positions: new CallbackProperty(() => {
        const am = getAM()
        if (!am || am.gridLines.length === 0 || am.polygon.length < 3) return []
        const alt = getAlt()
        const last = am.gridLines[am.gridLines.length - 1]
        const centroid = getPolygonCentroid(am.polygon)
        return [
          Cartesian3.fromDegrees(last.end.longitude, last.end.latitude, alt),
          Cartesian3.fromDegrees(centroid.lon, centroid.lat, alt),
        ]
      }, false),
      show: new CallbackProperty(() => {
        const am = getAM()
        return !!(am && am.gridLines.length > 0 && am.polygon.length >= 3)
      }, false) as any,
      width: 3,
      material: AREA_PATH_COLOR,
      arcType: ArcType.NONE,
    },
  })

  // ── Start marker ──
  _add(`area-start-${missionId}`, {
    position: new CallbackProperty(() => {
      const am = getAM()
      if (!am || am.gridLines.length === 0) return Cartesian3.ZERO
      const s = am.gridLines[0].start
      return Cartesian3.fromDegrees(s.longitude, s.latitude, getAlt())
    }, false) as unknown as PositionProperty,
    show: new CallbackProperty(() => {
      const am = getAM()
      return !!(am && am.gridLines.length > 0)
    }, false) as any,
    point: {
      color: AREA_START_COLOR,
      pixelSize: 14,
      outlineWidth: 3,
      outlineColor: Color.WHITE,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
    label: {
      text: 'START',
      font: 'bold 11px sans-serif',
      fillColor: Color.WHITE,
      outlineColor: Color.BLACK,
      outlineWidth: 2,
      style: 2,
      verticalOrigin: 1,
      pixelOffset: new Cartesian2(0, -18),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  })

  return entityIds
}

/**
 * Remove all area mission entities for a given mission.
 */
export const removeAreaMissionEntities = (missionId: string) => {
  removeEntity(`area-poly-fill-${missionId}`)
  removeEntity(`area-poly-outline-${missionId}`)
  const MAX_GRID_LINES = 200
  for (let i = 0; i < MAX_GRID_LINES; i++) {
    removeEntity(`area-line-${missionId}-${i}`)
  }
  for (let i = 0; i < MAX_GRID_LINES - 1; i++) {
    removeEntity(`area-conn-${missionId}-${i}`)
  }
  removeEntity(`area-centroid-${missionId}`)
  removeEntity(`area-start-${missionId}`)
  areaEntityCounts.delete(missionId)
}

export const loadMissionEntities = (mission: Mission) => {
  const { missions, activeMissionIndex } = useMissionsState()
  if (!mission.isVisible) return
  const missionIndex = missions.value.findIndex((m) => m.missionId === mission.missionId)

  if (mission.missionType === 'area' && (mission.areaMission?.polygon?.length ?? 0) >= 3) {
    // Same path for ALL area missions (active or not) — like addWaypointEntity.
    // CallbackProperty entities read from the store; no edit/view distinction.
    const existing = missions.value[missionIndex].waypointEntities.find(
      (e) => e.id === `area-${mission.missionId}`,
    )
    if (!existing) {
      const entityIds = addAreaMissionEntities(mission.missionId)
      if (entityIds.length > 0) {
        missions.value[missionIndex].waypointEntities.push({
          id: `area-${mission.missionId}`,
          entities: entityIds,
        })
      }
    }
    return
  }

  if (mission.elements.length === 0) return

  mission.elements.forEach((element) => {
    if (element.type === 'waypoint') {
      const entities = addWaypointEntity(mission.missionId, element.id)
      missions.value[missionIndex].waypointEntities.push({
        id: element.id,
        ...entities,
      })
    } else if (element.type === 'circle') {
      const entity = addCircleEntity(mission.missionId, element.id)
      missions.value[missionIndex].waypointEntities.push({
        id: element.id,
        ...entity,
      })
    }
  })
  if (mission.elements.length > 1) {
    for (let i = 0; i < mission.elements.length - 1; i++) {
      drawConnectionBetween(mission.elements[i].id, mission.elements[i + 1].id, mission)
    }
  }
}

/**
 * Remove all map entities for a single mission (waypoints, area polygon/grid, connection lines).
 * Clears mission.waypointEntities. Use before replacing a mission (e.g. discard changes).
 */
export const clearMissionEntities = (mission: Mission) => {
  if (!viewer.value) return
  const { missions } = useMissionsState()
  const missionIndex = missions.value.findIndex((m) => m.missionId === mission.missionId)
  if (missionIndex < 0) return

  const missionRef = missions.value[missionIndex]

  // Area missions: use dedicated cleanup (handles pre-allocated slots efficiently)
  if (mission.missionType === 'area') {
    removeAreaMissionEntities(mission.missionId)
  }

  missionRef.waypointEntities.forEach((collection) => {
    Object.entries(collection).forEach(([key, value]) => {
      if (key === 'id') return
      if (Array.isArray(value)) {
        value.forEach((eid) => removeEntity(eid))
      } else if (typeof value === 'string') {
        removeEntity(value)
      }
    })
  })
  missionRef.waypointEntities = []

  for (let i = 0; i < mission.elements.length - 1; i++) {
    const a = mission.elements[i]
    const b = mission.elements[i + 1]
    removeEntity(`horizontal-line-${a.id}-${b.id}`)
  }
}

/**
 * Set visibility of all Cesium entities for a mission and update its `isVisible` flag.
 * Keeps the data flag and actual entity `.show` in sync — use instead of setting
 * `mission.isVisible` directly.
 */
export const setMissionEntityVisibility = (missionIndex: number, visible: boolean) => {
  const { missions } = useMissionsState()
  const mission = missions.value[missionIndex]
  if (!mission) return

  mission.isVisible = visible

  if (!viewer.value) return

  // If toggling on and entities don't exist yet, create them
  if (visible) {
    const hasContent =
      mission.elements.length > 0 ||
      (mission.missionType === 'area' && (mission.areaMission?.polygon?.length ?? 0) >= 3)
    if (hasContent && mission.waypointEntities.length === 0) {
      loadMissionEntities(mission)
      return
    }
  }

  // Toggle waypointEntities show/hide
  if (mission.waypointEntities?.length > 0) {
    mission.waypointEntities.forEach((entityCollection) => {
      Object.entries(entityCollection).forEach(([key, value]) => {
        if (key === 'id') return
        if (Array.isArray(value)) {
          value.forEach((entityId) => {
            const entity = viewer.value?.entities.getById(entityId)
            if (entity) entity.show = visible
          })
        } else if (typeof value === 'string') {
          const entity = viewer.value?.entities.getById(value)
          if (entity) entity.show = visible
        }
      })
    })
  }

  // Toggle connection lines between waypoint elements
  if (mission.elements.length > 1) {
    for (let i = 0; i < mission.elements.length - 1; i++) {
      const el1 = mission.elements[i]
      const el2 = mission.elements[i + 1]
      const lineId = `horizontal-line-${el1.id}-${el2.id}`
      const line = viewer.value?.entities.getById(lineId)
      if (line) line.show = visible
    }
  }

  // Fallback: toggle entities by known ID patterns
  mission.elements.forEach((element) => {
    const patterns = [
      `ellipsoid-base-${element.id}`,
      `vertical-line-${element.id}`,
      `ellipsoid-top-${element.id}`,
      `circle-top-${element.id}`,
    ]
    patterns.forEach((pattern) => {
      const entity = viewer.value?.entities.getById(pattern)
      if (entity) entity.show = visible
    })
  })
}

export const updateEntityColors = () => {
  if (!viewer.value) return

  const { missions, activeMissionIndex } = useMissionsState()

  missions.value.forEach((mission, missionIndex) => {
    const isActiveMission = missionIndex === activeMissionIndex.value
    const colors = isActiveMission ? ACTIVE_ROUTE_COLORS : WAYPOINT_COLORS

    mission.elements.forEach((element) => {
      if (viewer.value) {
        const entities = viewer.value.entities.values

        for (let i = 0; i < entities.length; i++) {
          const entity = entities[i]
          const entityId = entity.id

          if (!entityId.includes(element.id)) continue

          if (entity.ellipsoid) {
            entity.ellipsoid.material = new ColorMaterialProperty(colors.base)
          } else if (entity.polyline) {
            if (entityId.includes('vertical-line')) {
              entity.polyline.material = new ColorMaterialProperty(colors.line)
            } else if (entityId.includes('horizontal-line')) {
              entity.polyline.material = new PolylineArrowMaterialProperty(colors.connection)
            } else if (entityId.includes('circle-top') && element.type === 'circle') {
              entity.polyline.material = new ColorMaterialProperty(
                new Color(
                  colors.base.red * 0.7,
                  colors.base.green * 0.7,
                  colors.base.blue * 0.7,
                  1.0,
                ),
              )
            }
          }
        }
      }
    })

    if (mission.elements.length > 1 && viewer.value) {
      for (let i = 0; i < mission.elements.length - 1; i++) {
        const element1 = mission.elements[i]
        const element2 = mission.elements[i + 1]
        const lineId = `horizontal-line-${element1.id}-${element2.id}`
        const line = viewer.value.entities.getById(lineId)

        if (line && line.polyline) {
          line.polyline.material = new PolylineArrowMaterialProperty(colors.connection)
        }
      }
    }
  })
}

export const drawConnectionBetween = (
  elementId1: string,
  elementId2: string,
  mission: Mission,
  connectionColor = Color.fromCssColorString('#0ADA4580'),
) => {
  if (!viewer.value || !mission) {
    log.warn('Cannot draw connection: Missing viewer or mission')
    return undefined
  }

  try {
    const element1 = mission.elements.find((e) => e.id === elementId1)
    const element2 = mission.elements.find((e) => e.id === elementId2)

    if (!element1 || !element2) {
      log.warn('Cannot draw connection: Elements not found')
      return undefined
    }

    const { activeMission } = useMissionsState()

    const lineId = `horizontal-line-${elementId1}-${elementId2}`
    removeEntity(lineId)

    const isActiveMission =
      activeMission.value && mission.missionName === activeMission.value.missionName

    const defaultColor = Color.fromCssColorString('#0ADA4580')
    if (isActiveMission && Color.equals(connectionColor, defaultColor)) {
      connectionColor = ACTIVE_ROUTE_COLORS.connection
    }

    const connection = viewer.value.entities.add({
      id: lineId,
      polyline: {
        positions: new CallbackProperty(() => {
          const currentElement1 = mission.elements.find((e) => e.id === elementId1)
          const currentElement2 = mission.elements.find((e) => e.id === elementId2)

          if (
            !currentElement1 ||
            !currentElement2 ||
            !currentElement1.topPosition ||
            !currentElement2.topPosition
          ) {
            return undefined
          }

          return [currentElement1.topPosition, currentElement2.topPosition]
        }, false),
        width: 12,
        material: new PolylineArrowMaterialProperty(connectionColor),
        arcType: ArcType.NONE,
      },
    })

    return connection
  } catch (error) {
    log.error('Error creating connection line:', error)
    return undefined
  }
}
export const connectToPreviousElement = (elementId: string) => {
  const { missions, activeMissionIndex, activeMission } = useMissionsState()
  if (!activeMission.value || activeMissionIndex.value === null) return

  const mission = missions.value[activeMissionIndex.value]
  const elements = mission.elements

  const currentElementIndex = elements.findIndex((element) => element.id === elementId)
  if (currentElementIndex > 0) {
    const previousElement = elements[currentElementIndex - 1]
    drawConnectionBetween(previousElement.id, elementId, mission)
  }
}

export const clearAllEntities = () => {
  if (!viewer.value) return

  try {
    const entities = viewer.value.entities.values
    const entitiesToRemove = []

    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i]
      const entityId = entity.id

      if (
        entityId.includes('ellipsoid-base-') ||
        entityId.includes('vertical-line-') ||
        entityId.includes('ellipsoid-top-') ||
        entityId.includes('circle-top-') ||
        entityId.includes('horizontal-line-') ||
        entityId.startsWith('area-poly-') ||
        entityId.startsWith('area-line-') ||
        entityId.startsWith('area-conn-') ||
        entityId.startsWith('area-centroid-') ||
        entityId.startsWith('area-start-') ||
        entityId.includes('area-polygon-fill-') ||
        entityId.includes('area-polygon-outline-') ||
        entityId.includes('area-flight-line-') ||
        entityId.includes('area-flight-conn-') ||
        entityId.includes('area-flight-start-')
      ) {
        entitiesToRemove.push(entity)
      }
    }

    for (const entity of entitiesToRemove) {
      viewer.value.entities.remove(entity)
    }
  } catch (error) {
    log.error('Error clearing entities:', error)
  }
}

export const addBaseEllipsoid = (position: Cartesian3, id: string) => {
  try{
    if(!viewer.value) return
    removeEntity(id)
    const entity = viewer.value.entities.add({
      id: id,
      position: position,
      ellipsoid: {
        radii: new Cartesian3(1, 1, 1),
        material: ACTIVE_ROUTE_COLORS.base
      },
    })
    return entity.id
  }catch(err){
    log.error('error adding base ellipsoid', err)
  }
}
export const addTopEllipsoid = (position: Cartesian3, id: string) => {
  try{
    if(!viewer.value) return
    removeEntity(id)
    const entity = viewer.value.entities.add({
      id: id,
      position: position,
      ellipsoid: {
        radii: new Cartesian3(2, 2, 2),
        material: ACTIVE_ROUTE_COLORS.base
      },
    })
    return entity.id
  }catch(err){
    log.error('error adding top ellipsoid', err)
  }
}
export const addVerticalLine = (position1: Cartesian3, position2: Cartesian3, id: string) => {
  try{
    if(!viewer.value) return
    removeEntity(id)
    const entity = viewer.value.entities.add({
      id: id,
      polyline: {
        positions: [position1, position2],
        material: ACTIVE_ROUTE_COLORS.line,
        width: 3,
      },
    })
    return entity.id
  }catch(err){
    log.error('error adding vertical line', err)
  }
}

export const connectWaypoints = (position1: Cartesian3, position2: Cartesian3, id: string) => {
  try{
    if(!viewer.value) return 
    removeEntity(id)
    const entity = viewer.value.entities.add({
      id: id,
      polyline: {
        positions: [position1, position2],
        material: ACTIVE_ROUTE_COLORS.connection,
        width: 3,
      },
    })
    return entity.id
  }catch(err){
    log.error('error connecting waypoints line', err)
  }
}
